import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchJson } from "@/lib/api";
import { orderListQuery, wholesaleQuery } from "@/lib/wholesale-query";
import { stockRefetchInterval } from "@/lib/wholesale-stock-progress";
import type { CatalogCategory } from "@/services/wholesale-catalog";
import type { WholesaleOrder, Shipping, CourierShipment } from "@/services/wholesale-orders";
import type { Partner, PartnerBalance, CustomerHit } from "@/services/wholesale-partners";
import type { Payment } from "@/services/wholesale-payments";
import type { ItemSnapshot, CatalogPage, CatalogProductDetail, CatalogSort } from "@/services/wholesale-catalog";
import type { EditorItemInput } from "@/lib/wholesale-items";
import type { Issuer, IssuerInput } from "@/services/wholesale-issuer";
import type { WholesaleAnalytics } from "@/services/wholesale-analytics";

const json = (method: string, body?: unknown): RequestInit => ({
  method,
  headers: { "Content-Type": "application/json" },
  body: body === undefined ? undefined : JSON.stringify(body),
});

function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ["wholesale"] });
}

/** Greška s HTTP statusom — stranice po njoj razlikuju „nije pronađen" (404) od ostalih grešaka. */
export class HttpError extends Error {
  constructor(message: string, public status: number) {
    super(message);
    this.name = "HttpError";
  }
}

async function fetchJsonWithStatus<T>(url: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new HttpError(body.error || res.statusText, res.status);
  }
  return res.json();
}

// ---------- tipovi ----------

export type OrderAction = "confirm" | "cancel" | "ship" | "complete" | "stock/retry" | `emails/${string}`;

/** Stavka liste narudžbi: zaglavlje bez stavki, s brojem stavki i uplaćenim iznosom. */
export type WholesaleOrderListItem = Omit<WholesaleOrder, "items" | "stockJobs" | "emails"> & {
  itemCount: number;
  paid: number;
};

/** Detalj narudžbe kako ga vraća `GET /api/wholesale/orders/:id` (s uplatama i kurirskom pošiljkom). */
export type WholesaleOrderDetail = WholesaleOrder & { paid: number; payments: Payment[]; shipment: CourierShipment | null };

export type PartnerListItem = Partner & { balance: number; overdueCount: number };

export type PartnerSaveInput = Partial<Omit<Partner, "id" | "createdAt" | "updatedAt">> & { id?: string; name: string };

export type CreateOrderInput = {
  partnerId: string;
  shipping?: Partial<Shipping>;
  note?: string;
  customerNote?: string;
  items?: EditorItemInput[];
};

export type UpdateOrderInput = {
  id: string;
  shipping?: Partial<Shipping>;
  note?: string;
  customerNote?: string;
};

export type PaymentInputBody = {
  partnerId: string;
  orderId: string | null;
  amount: number;
  method: "bank" | "cash";
  paidAt: number;
  note?: string;
};

export type { WholesaleOrder, Partner, PartnerBalance, Payment, CustomerHit, ItemSnapshot, Shipping };

// ---------- postavke ----------

export type WholesaleSettings = {
  vatRate: number;
  /** Prazno = svi shopovi. */
  defaultStoreId: string;
  sources: { id: string; name: string }[];
  candidates: { id: string; name: string; syncMode: string; storeType: string }[];
};

export type ShareLink = { token: string; url: string; expiresAt: number };

export type WholesaleContext = {
  sources: { id: string; name: string }[];
  vatRate: number;
  /** Shop unaprijed odabran u filterima; prazno = svi shopovi. */
  defaultStoreId: string;
};

/** Napomena: `GET /api/wholesale/settings` traži admina — koristi se samo na stranici postavki.
 *  Ostale stranice koriste `useWholesaleContext` koji radi i za ulogu „veleprodaja". */
export const useWholesaleSettings = () =>
  useQuery({
    queryKey: ["wholesale", "settings"],
    queryFn: () => fetchJson<WholesaleSettings>("/api/wholesale/settings"),
    retry: false,
  });

/** Shopovi uključeni u veleprodaju i PDV stopa — dostupno adminu i ulozi „veleprodaja". */
export const useWholesaleContext = () =>
  useQuery({
    queryKey: ["wholesale", "context"],
    queryFn: () => fetchJson<WholesaleContext>("/api/wholesale/context"),
    staleTime: 60_000,
  });

/** Filter shopa koji kreće od zadanog shopa iz postavki. Dok korisnik sam ne izabere,
 *  vrijednost prati kontekst — pa izbor ne ostane na „svi shopovi" kad kontekst stigne
 *  tek nakon prvog rendera. `all` je vrijednost kojom komponenta označava sve shopove. */
export function useStoreFilter(all: string) {
  const ctx = useWholesaleContext();
  const [picked, setPicked] = useState<string | null>(null);
  const defaultStore = ctx.data?.defaultStoreId || all;
  return { store: picked ?? defaultStore, setStore: setPicked as (id: string) => void, defaultStore };
}

export function useSaveWholesaleSettings() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (b: { vatRate?: number; sourceStoreIds?: string[]; defaultStoreId?: string }) =>
      fetchJson<WholesaleSettings>("/api/wholesale/settings", json("PUT", b)),
    onSuccess: inv,
  });
}

// ---------- izdavalac ----------

export type { Issuer, IssuerInput };

/** Podaci firme na predračunu, otpremnici i javnom linku. */
export const useIssuer = () =>
  useQuery({
    queryKey: ["wholesale", "issuer"],
    queryFn: () => fetchJson<Issuer>("/api/wholesale/issuer"),
    retry: false,
  });

export function useSaveIssuer() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (b: IssuerInput) => fetchJson<Issuer>("/api/wholesale/issuer", json("PUT", b)),
    onSuccess: inv,
  });
}

/** Logo se šalje odmah po izboru fajla; `file === null` ga briše. */
export function useSaveIssuerLogo() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: async (file: File | null) => {
      if (!file) return fetchJson<Issuer>("/api/wholesale/issuer/logo", { method: "DELETE" });
      const form = new FormData();
      form.append("file", file);
      return fetchJson<Issuer>("/api/wholesale/issuer/logo", { method: "POST", body: form });
    },
    onSuccess: inv,
  });
}

// ---------- partneri ----------

export const usePartners = (search = "") =>
  useQuery({
    queryKey: ["wholesale", "partners", search],
    queryFn: () => fetchJson<PartnerListItem[]>(`/api/wholesale/partners?${wholesaleQuery({ search })}`),
  });

export const usePartner = (id?: string) =>
  useQuery({
    queryKey: ["wholesale", "partner", id],
    queryFn: () => fetchJsonWithStatus<Partner>(`/api/wholesale/partners/${id}`),
    enabled: !!id,
    retry: (count, err) => !(err instanceof HttpError && err.status === 404) && count < 2,
  });

export const usePartnerBalance = (id?: string) =>
  useQuery({
    queryKey: ["wholesale", "partner-balance", id],
    queryFn: () => fetchJson<PartnerBalance>(`/api/wholesale/partners/${id}/balance`),
    enabled: !!id,
  });

export const usePartnerPayments = (id?: string) =>
  useQuery({
    queryKey: ["wholesale", "partner-payments", id],
    queryFn: () => fetchJson<Payment[]>(`/api/wholesale/partners/${id}/payments`),
    enabled: !!id,
  });

export function useSavePartner() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ id, ...b }: PartnerSaveInput) =>
      fetchJson<Partner>(id ? `/api/wholesale/partners/${id}` : "/api/wholesale/partners", json(id ? "PUT" : "POST", b)),
    onSuccess: inv,
  });
}

export function useDeletePartner() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => fetchJson(`/api/wholesale/partners/${id}`, { method: "DELETE" }),
    onSuccess: inv,
  });
}

export const useCustomerSearch = (q: string) =>
  useQuery({
    queryKey: ["wholesale", "customer-search", q],
    queryFn: () => fetchJson<CustomerHit[]>(`/api/wholesale/customers/search?${wholesaleQuery({ q })}`),
    enabled: q.trim().length >= 2,
  });

export type PortalPassword = { email: string; password: string; emailSent: boolean; emailError?: string };

/** Lozinka za partnerski portal — ne mijenja podatke partnera, pa se ništa ne invalidira.
 *  Odgovor se nigdje ne kešira: lozinka se vraća samo jednom. */
export function useGeneratePortalPassword() {
  return useMutation({
    mutationFn: (v: { id: string; sendEmail: boolean }) =>
      fetchJson<PortalPassword>(`/api/wholesale/partners/${v.id}/portal-password`, json("POST", { sendEmail: v.sendEmail })),
  });
}

export function useImportPartner() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (b: { storeId: string; wcCustomerId: number }) =>
      fetchJson<{ partner: Partner; existing: boolean }>("/api/wholesale/partners/import", json("POST", b)),
    onSuccess: inv,
  });
}

// ---------- katalog ----------

export const useCatalog = (q: string, storeId = "") =>
  useQuery({
    queryKey: ["wholesale", "catalog", q, storeId],
    queryFn: () => fetchJson<ItemSnapshot[]>(`/api/wholesale/catalog?${wholesaleQuery({ q, storeId })}`),
    enabled: q.trim().length >= 2,
  });

/** Pregled kataloga po stranicama — puna lista, filteri i sort idu na server jer
 *  katalog zna imati desetine hiljada stavki. */
export const useCatalogBrowse = (f: {
  q: string; storeId: string; inStock: boolean; sort: CatalogSort; page: number; perPage?: number;
  categorySlugs?: string[];
}) =>
  useQuery({
    queryKey: ["wholesale", "catalog-browse", f],
    queryFn: () => fetchJson<CatalogPage>(`/api/wholesale/catalog/browse?${wholesaleQuery({
      q: f.q, storeId: f.storeId, inStock: f.inStock ? "1" : "", sort: f.sort, page: f.page, perPage: f.perPage,
      category: f.categorySlugs ?? [],
    })}`),
    placeholderData: (prev) => prev,
  });

export const useCatalogCategories = (storeId?: string) =>
  useQuery({
    queryKey: ["wholesale", "catalog-categories", storeId ?? ""],
    queryFn: () => fetchJson<CatalogCategory[]>(`/api/wholesale/catalog/categories?${wholesaleQuery({ storeId })}`),
  });

export const useCatalogProduct = (storeId?: string, wcProductId?: number) =>
  useQuery({
    queryKey: ["wholesale", "catalog-product", storeId, wcProductId],
    queryFn: () => fetchJson<CatalogProductDetail>(
      `/api/wholesale/catalog/product?${wholesaleQuery({ storeId, productId: wcProductId })}`,
    ),
    enabled: !!storeId && !!wcProductId,
  });

// ---------- narudžbe ----------

export const useWholesaleOrders = (f: { status?: string; partnerId?: string; search?: string }) =>
  useQuery({
    queryKey: ["wholesale", "orders", f],
    queryFn: () => fetchJson<WholesaleOrderListItem[]>(`/api/wholesale/orders?${orderListQuery(f)}`),
  });

export const useWholesaleOrder = (id?: string) =>
  useQuery({
    queryKey: ["wholesale", "order", id],
    queryFn: () => fetchJson<WholesaleOrderDetail>(`/api/wholesale/orders/${id}`),
    enabled: !!id,
    // Živi napredak upisa zalihe: osvježavaj dok ijedan posao čeka, pa stani.
    refetchInterval: (query) => stockRefetchInterval(query.state.data?.stockJobs),
  });

export function useCreateWholesaleOrder() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (b: CreateOrderInput) => fetchJson<WholesaleOrder>("/api/wholesale/orders", json("POST", b)),
    onSuccess: inv,
  });
}

export function useUpdateWholesaleOrder() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ id, ...b }: UpdateOrderInput) => fetchJson<WholesaleOrder>(`/api/wholesale/orders/${id}`, json("PUT", b)),
    onSuccess: inv,
  });
}

export function useSetWholesaleItems() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ id, items }: { id: string; items: EditorItemInput[] }) =>
      fetchJson<WholesaleOrder>(`/api/wholesale/orders/${id}/items`, json("PUT", { items })),
    onSuccess: inv,
  });
}

export type OrderActionResult = Partial<WholesaleOrder> & { order?: WholesaleOrder; warnings?: string[] };

export function useOrderAction() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ id, action, body }: { id: string; action: OrderAction; body?: Record<string, unknown> }) =>
      fetchJson<OrderActionResult>(`/api/wholesale/orders/${id}/${action}`, json("POST", body ?? {})),
    onSuccess: inv,
  });
}

// ---------- javni link ----------

/** Traži link na predračun; server vraća isti link dok postojeći važi. */
export function useShareLink() {
  return useMutation({
    mutationFn: (id: string) => fetchJson<ShareLink>(`/api/wholesale/orders/${id}/share`, json("POST")),
  });
}

export function useRevokeShareLink() {
  return useMutation({
    mutationFn: (id: string) => fetchJson(`/api/wholesale/orders/${id}/share`, { method: "DELETE" }),
  });
}

// ---------- uplate ----------

export function useCreatePayment() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (b: PaymentInputBody) => fetchJson<Payment>("/api/wholesale/payments", json("POST", b)),
    onSuccess: inv,
  });
}

export function useDeletePayment() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => fetchJson(`/api/wholesale/payments/${id}`, { method: "DELETE" }),
    onSuccess: inv,
  });
}

/** VP analitika za period (unix sekunde, obje granice uključene). Stari podaci ostaju na
 *  ekranu dok se novi period učitava, da stranica ne trepće pri promjeni perioda. */
export const useWholesaleAnalytics = (from: number, to: number) =>
  useQuery({
    queryKey: ["wholesale", "analytics", from, to],
    queryFn: () =>
      fetchJson<WholesaleAnalytics>(`/api/wholesale/analytics?${wholesaleQuery({ from, to, tz: new Date(from * 1000).getTimezoneOffset() })}`),
    placeholderData: (prev) => prev,
    staleTime: 60_000,
  });
